import { cloneDeep } from "lodash";
import type { FormValues } from "@/widgets/classic-form/form";
import { RELATION_DEFAULT_INSTANCE, applyExactNameDefaults } from "@/widgets/classic-form/form";
import type { RelationInstance, Relations } from "@/entities/data-model";
import type { RelationshipFieldGroup } from "@/entities/form-metadata";
import { NameAssertion } from "@/entities/form-metadata";

const mapInstanceWithExactness = ({
    instance,
    isFirstNameExactnessEnabled,
    isSurnameExactnessEnabled,
    matchAllTermsExactly,
    isEditForm,
}: {
    instance: RelationInstance;
    isFirstNameExactnessEnabled: boolean;
    isSurnameExactnessEnabled: boolean;
    matchAllTermsExactly: boolean;
    isEditForm: boolean;
}): RelationInstance => {
    // Edit-form prefill keeps the user's choices; forceExact never applies there.
    const forceExact = matchAllTermsExactly && !isEditForm;
    // Deep copy: members must not share exactness objects, or toggling one member toggles all.
    return applyExactNameDefaults(cloneDeep(instance), {
        exactGivenName: forceExact && isFirstNameExactnessEnabled,
        exactSurname: forceExact && isSurnameExactnessEnabled,
    });
};

export const populateFamilyMemberFields = ({
    fieldGroup,
    dataModel,
    formValuesFamilyData,
    isEditForm,
    matchAllTermsExactly,
}: {
    fieldGroup: RelationshipFieldGroup;
    dataModel: FormValues;
    formValuesFamilyData: FormValues["family"];
    isEditForm?: boolean;
    matchAllTermsExactly: boolean;
}) => {
    const { fieldsetDisplayStrategy } = fieldGroup;
    const prefilled = fieldsetDisplayStrategy !== "empty-dynamic";
    const removable = fieldsetDisplayStrategy !== "prefilled-static";
    if (!isEditForm && !prefilled && removable) return;
    fieldGroup.fieldsets.forEach((relationItem) => {
        const { relation } = relationItem;

        const { isFirstNameExactnessEnabled, isSurnameExactnessEnabled } = relationItem.fields.reduce(
            (acc, field) => {
                const {
                    assertion,
                    features: { showExactness },
                } = field;
                if (assertion === NameAssertion.GivenName) {
                    acc.isFirstNameExactnessEnabled = showExactness ?? true;
                } else if (assertion === NameAssertion.Surname) {
                    acc.isSurnameExactnessEnabled = showExactness ?? true;
                }
                return acc;
            },
            { isFirstNameExactnessEnabled: true, isSurnameExactnessEnabled: true },
        );

        const relationKey = relation.toLowerCase() as keyof Relations;
        const defaultValue = dataModel.family[relationKey].instances;

        if (formValuesFamilyData[relationKey]?.instances.length) return;
        if (defaultValue.length) {
            const mappedInstances = defaultValue.map((instance) =>
                mapInstanceWithExactness({
                    instance,
                    isFirstNameExactnessEnabled,
                    isSurnameExactnessEnabled,
                    matchAllTermsExactly,
                    isEditForm: isEditForm ?? false,
                }),
            );
            formValuesFamilyData[relationKey].instances.push(...mappedInstances);
        } else if (!removable || prefilled) {
            const mappedInstance = mapInstanceWithExactness({
                instance: RELATION_DEFAULT_INSTANCE,
                isFirstNameExactnessEnabled,
                isSurnameExactnessEnabled,
                matchAllTermsExactly,
                isEditForm: isEditForm ?? false,
            });
            formValuesFamilyData[relationKey].instances.push(mappedInstance);
        }
    });
};
